import React from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import * as Y from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { useGetMutateCall } from '../../lib/api/get'
import { useCreateCall } from '../../lib/api/create'
import { useDeleteCall } from '../../lib/api/delete'

import invalid from '../../assets/images/invalid.svg'

export const DocManager = ({
  setOpenFormModal,
  modSchema,
  defaultData,
  setIsViewOnly,
  isLocalEnvironment,
}) => {
  const getCallMutation = useGetMutateCall()
  const createCallMutation = useCreateCall()
  const deleteCallMutation = useDeleteCall()
  const [deleteConfirm, setDeleteConfirm] = React.useState(false)
  const [deleteData, setDeleteData] = React.useState(null)
  const validation = Y.object().shape({
    document: Y.mixed().required('Choose a document to upload!'),
    docType: Y.string().required('Select Doc Type!'),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validation),
  })
  const [activeTab, setActiveTab] = React.useState(0)

  const onFormSubmit = async (data) => {
    let formData = new FormData()
    formData.append('docType', data.docType)
    formData.append('file', data.document[0])
    formData.append('applicationId', defaultData.applicationId)

    await createCallMutation.mutateAsync({
      url: `${isLocalEnvironment}`,
      apiEndpoint: `upload?endPoint=applicationDocument&applicationId=${defaultData.applicationId}`,
      parentAPI: modSchema.api,
      data: formData,
      headers: {
        tokenSource: 'passport',
        Authorization: `Bearer ${authDetails.authentication.tokenObject}`,
      },
      params: '',
      messageTitle: 'Document',
      message: 'Document uploaded successfully!',
    })
  }

  const getDoc = async (doc, type) => {
    await getCallMutation
      .mutateAsync({
        apiURL: isLocalEnvironment + '/getData',
        apiEndpoint: type === 'downloads' ? 'applicationDocType' : 'applicationDocument',
        id: type === 'downloads' ? doc.applicationDocTypeId : doc.applicationDocumentsId,
        params: '',
        isDynamic: true,
      })
      .then((resp) => {
        const link = document.createElement('a')
        link.setAttribute('href', resp.formData?.fileURL)
        link.setAttribute('target', '_blank')

        document.body.appendChild(link)

        link.click()
      })
  }

  const deleteDoc = (doc) => {
    setDeleteConfirm(true)
    setDeleteData(doc)
  }

  const closeDelete = () => {
    setDeleteConfirm(false)
    setDeleteData(null)
  }

  const onDelete = async () => {
    await deleteCallMutation
      .mutateAsync({
        url: isLocalEnvironment + '/deleteData',
        apiEndpoint: 'applicationDocument',
        parentAPI: modSchema.api,
        id: deleteData.applicationDocumentsId,
        messageTitle: 'Uploaded Documents',
        message: 'Document deleted!',
        isDynamic: true,
        params: '',
      })
      .then(() => {
        setDeleteConfirm(false)
        setDeleteData(null)
      })
  }

  const getAuth = sessionStorage.getItem('auth') ? JSON.parse(sessionStorage.getItem('auth')) : null
  const authDetails = getAuth.state

  return (
    <div
      className="trtui-modal trtui-modal-xl trtui-fade trtui-show"
      aria-labelledby="exampleModalLabel"
      style={{ display: 'block', backgroundColor: 'rgba(55,55,55,0.3)' }}
      aria-modal="true"
      role="dialog"
    >
      <div className="trtui-modal-dialog trtui-modal-xl">
        <div className="trtui-modal-content">
          <div className="trtui-modal-header trtui-bg-light trtui-p-3">
            <h5 className="trtui-modal-title" id="exampleModalLabel">
              {modSchema.fileManager.title}
            </h5>
            <button
              type="button"
              className="trtui-btn-close"
              onClick={() => {
                setOpenFormModal(false)
                setIsViewOnly(false)
              }}
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div className="trtui-card-header trtui-pt-3">
            <ul
              className="trtui-nav trtui-nav-tabs-custom trtui-rounded trtui-card-header-tabs trtui-border-bottom-0"
              role="tablist"
            >
              <li className="trtui-nav-item" role="presentation">
                <span
                  className={`trtui-nav-link trtui-cursor-pointer ${
                    activeTab === 0 ? 'trtui-active' : ''
                  }`}
                  data-bs-toggle="tab"
                  role="tab"
                  onClick={() => setActiveTab(0)}
                >
                  Downloads
                </span>
              </li>
              <li className="trtui-nav-item" role="presentation">
                <span
                  className={`trtui-nav-link trtui-cursor-pointer ${
                    activeTab === 1 ? 'trtui-active' : ''
                  }`}
                  data-bs-toggle="tab"
                  role="tab"
                  onClick={() => setActiveTab(1)}
                >
                  Uploads
                </span>
              </li>
            </ul>
          </div>
          <div className="trtui-modal-body">
            <div className="trtui-tab-content">
              <div
                className={`trtui-tab-pane ${activeTab === 0 ? 'trtui-active' : ''}`}
                role="tabpanel"
              >
                <div className="trtui-vstack trtui-gap-2">
                  {authDetails !== null &&
                    authDetails.dependeciesFormAPIS.applicationDocType?.formData &&
                    authDetails.dependeciesFormAPIS.applicationDocType?.formData.length > 0 &&
                    authDetails.dependeciesFormAPIS.applicationDocType?.formData.map(
                      (doc, docIndex) => (
                        <div
                          key={docIndex}
                          className="trtui-border trtui-rounded trtui-border-dashed trtui-p-2"
                        >
                          <div className="trtui-d-flex trtui-align-items-center">
                            <div className="trtui-flex-shrink-0 trtui-me-3">
                              <div className="trtui-avatar-sm">
                                <div className="trtui-avatar-title trtui-bg-light trtui-text-secondary trtui-rounded trtui-fs-24">
                                  <i className="ri-file-pdf-2-line" />
                                </div>
                              </div>
                            </div>
                            <div className="trtui-flex-grow-1 trtui-overflow-hidden">
                              <h5 className="trtui-fs-13 trtui-mb-1">
                                <span className="trtui-text-body trtui-text-truncate trtui-d-block">
                                  {doc.docType}
                                </span>
                              </h5>
                              <div>{doc.originalFileName}</div>
                            </div>
                            <div className="trtui-flex-shrink-0 trtui-ms-2">
                              <div className="trtui-d-flex trtui-gap-1">
                                <span
                                  onClick={() => getDoc(doc, 'downloads')}
                                  className="trtui-btn trtui-btn-icon trtui-text-muted trtui-btn-sm trtui-fs-18 trtui-cursor-pointer"
                                >
                                  {getCallMutation.isLoading ? (
                                    <span
                                      className="trtui-spinner-border trtui-spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                  ) : (
                                    <i className="ri-download-2-line" />
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                </div>
              </div>
              <div
                className={`trtui-tab-pane ${activeTab === 1 ? 'trtui-active' : ''}`}
                role="tabpanel"
              >
                <form onSubmit={handleSubmit(onFormSubmit)} className="d-flex">
                  <select
                    className={`trtui-form-control trtui-mb-2 ${
                      errors['docType'] && 'trtui-is-invalid'
                    }`}
                    style={{
                      paddingRight: 'calc(1.5em + 1rem)',
                      backgroundImage: errors['docType'] && `url("${invalid}")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                      backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
                    }}
                    {...register('docType')}
                    defaultValue={defaultData ? defaultData['docType'] : null}
                  >
                    <option value={''}>Select Doc Type</option>
                    {authDetails.dependeciesFormAPIS.applicationDocType?.formData?.length > 0 &&
                      authDetails.dependeciesFormAPIS.applicationDocType?.formData.map(
                        (docType, docTypeInd) => (
                          <option key={docTypeInd} value={docType.docType}>
                            {docType.docType}
                          </option>
                        ),
                      )}
                  </select>
                  {errors['docType'] && (
                    <div className="trtui-invalid-feedback">{errors['docType'].message}</div>
                  )}
                  <input
                    {...register('document')}
                    className={`trtui-form-control trtui-mt-3 trtui-mb-3 ${errors['document'] && 'trtui-is-invalid'}`}
                    id="formSizeLarge"
                    type="file"
                  />
                  {errors['document'] && (
                    <div className="trtui-invalid-feedback">{errors['document'].message}</div>
                  )}
                  <button
                    type="submit"
                    className="trtui-btn trtui-btn-primary"
                    style={{
                      borderTopLeftRadius: '0px',
                      borderBottomLeftRadius: '0px',
                    }}
                  >
                    {createCallMutation.isLoading ? (
                      <>
                        <span
                          className="trtui-spinner-border trtui-spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>{' '}
                        Uploading...
                      </>
                    ) : (
                      <>Upload</>
                    )}
                  </button>
                </form>
                <hr />
                <div key={deleteConfirm} className="trtui-vstack trtui-gap-2">
                  {deleteConfirm ? (
                    <div>
                      <div className="trtui-mt-2 trtui-text-center">
                        <lord-icon
                          src="https://cdn.lordicon.com/gsqxdxog.json"
                          trigger="loop"
                          colors="primary:#f7b84b,secondary:#f06548"
                          style={{ width: '100px', height: '100px' }}
                        />
                        <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
                          <h4>Are you Sure ?</h4>
                          <p className="trtui-text-muted trtui-mx-4 trtui-mb-0">
                            Are you Sure You want to delete this?
                          </p>
                        </div>
                      </div>
                      <div className="trtui-d-flex trtui-gap-2 trtui-justify-content-center trtui-mt-4 trtui-mb-2">
                        <button
                          type="button"
                          className="trtui-btn trtui-w-sm trtui-btn-light"
                          disabled={deleteCallMutation.isLoading}
                          data-bs-dismiss="modal"
                          onClick={() => {
                            closeDelete()
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="trtui-btn trtui-w-sm trtui-btn-danger"
                          disabled={deleteCallMutation.isLoading}
                          onClick={() => onDelete()}
                        >
                          {deleteCallMutation.isLoading ? (
                            <span className="trtui-d-flex trtui-align-items-center">
                              <span className="trtui-flex-grow-1 trtui-me-2">Deleting...</span>
                              <span
                                className="trtui-spinner-border trtui-flex-shrink-0"
                                role="status"
                              >
                                <span className="trtui-visually-hidden">Deleting...</span>
                              </span>
                            </span>
                          ) : (
                            'Yes, Delete It!'
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    defaultData.documents?.length > 0 &&
                    defaultData.documents?.map((doc, docIndex) => (
                      <div
                        key={docIndex}
                        className="trtui-border trtui-rounded trtui-border-dashed trtui-p-2"
                      >
                        <div className="trtui-d-flex trtui-align-items-center">
                          <div className="trtui-flex-shrink-0 trtui-me-3">
                            <div className="trtui-avatar-sm">
                              <div className="trtui-avatar-title trtui-bg-light trtui-text-secondary trtui-rounded trtui-fs-24">
                                <i className="ri-file-pdf-2-line" />
                              </div>
                            </div>
                          </div>
                          <div className="trtui-flex-grow-1 trtui-overflow-hidden">
                            <h5 className="trtui-fs-13 trtui-mb-1">
                              <span className="trtui-text-body trtui-text-truncate trtui-d-block">
                                {doc.docType}
                              </span>
                            </h5>
                            <div>{doc.originalFileName}</div>
                          </div>
                          <div className="trtui-flex-shrink-0 trtui-ms-2">
                            <div className="trtui-d-flex trtui-gap-1">
                              <span
                                onClick={() => getDoc(doc, 'uploads')}
                                className="trtui-btn trtui-btn-icon trtui-text-muted trtui-btn-sm trtui-fs-18 trtui-cursor-pointer"
                              >
                                {getCallMutation.isLoading ? (
                                  <span
                                    className="trtui-spinner-border trtui-spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                ) : (
                                  <i className="ri-download-2-line" />
                                )}
                              </span>
                              <span
                                onClick={() => deleteDoc(doc)}
                                className="trtui-btn trtui-btn-icon trtui-text-danger trtui-btn-sm trtui-fs-18 trtui-cursor-pointer"
                              >
                                <i className="ri-delete-bin-line" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

DocManager.propTypes = {
  setOpenFormModal: PropTypes.func,
  modSchema: PropTypes.object,
  defaultData: PropTypes.object,
  setIsViewOnly: PropTypes.func,
  isLocalEnvironment: PropTypes.string,
}
